import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  HISTORY_ENABLED,
  HistoryError,
  deleteHistory,
  listHistory,
} from './historyClient';

// ── a signed-in reader's saved clips ─────────────────────────────────────────
// Public API:
//   const history = useHistory(auth, activeClipId);
//   history.clips     what is loaded so far, newest first
//   history.hasMore   an older page exists
//   history.retentionDays      how long clips are kept, once the list has loaded
//   history.showMore()         loads the next page
//   history.refresh()          reloads what is on screen, with fresh links
//   history.refreshAfterSave() the same after a generation; resolves true if a
//                              new clip appeared, false if none did, and null
//                              if the list could not be read
//   history.remove(clipId)     deletes a clip; rejects if that failed
//
// `activeClipId` is whatever is playing. A refresh keeps that clip's current
// play link, because swapping an <audio> element's src restarts it.

// The API's links last an hour. Refreshing inside that means a tap on Play or
// Download doesn't land on a dead one.
const LINK_REFRESH_MS = 50 * 60 * 1000;
const STALE_CHECK_MS = 60 * 1000;

const EMPTY = { clips: [], nextAfter: null, retentionDays: null };

// Enough pages to cover `wanted` clips, so a refresh after "Show more" keeps
// everything the reader has opened.
const fetchPages = async (token, wanted) => {
  const clips = [];
  let page;
  let after;
  do {
    // Each page depends on the one before it.
    // eslint-disable-next-line no-await-in-loop
    page = await listHistory(token, after);
    clips.push(...page.items);
    after = page.nextAfter;
  } while (after && clips.length < wanted);
  return {
    clips,
    nextAfter: after ?? null,
    retentionDays: page.retentionDays,
  };
};

const useHistory = (auth, activeClipId) => {
  const [state, setState] = useState(EMPTY);

  // The callbacks below are stable, so they reach the latest values through
  // refs. The page's own effects depend on them.
  const stateRef = useRef(EMPTY);
  const authRef = useRef(auth);
  const activeRef = useRef(activeClipId);
  const loadedAtRef = useRef(0);
  // Bumped on sign-in and sign-out, so a slow answer for the previous reader
  // can't land in the new reader's list.
  const sessionRef = useRef(0);

  authRef.current = auth;
  activeRef.current = activeClipId;

  const publish = useCallback(next => {
    stateRef.current = next;
    setState(next);
  }, []);

  // A token can go stale while a phone sleeps, so a 401 renews it once and
  // tries again.
  const withToken = useCallback(async call => {
    try {
      return await call(authRef.current.user?.access_token);
    } catch (err) {
      if (!(err instanceof HistoryError) || err.status !== 401) throw err;
      const user = await authRef.current.signinSilent();
      if (!user) throw err;
      return call(user.access_token);
    }
  }, []);

  const refresh = useCallback(async () => {
    const session = sessionRef.current;
    const wanted = stateRef.current.clips.length;
    const next = await withToken(token => fetchPages(token, wanted));
    if (session !== sessionRef.current) return null;

    const previous = stateRef.current.clips;
    loadedAtRef.current = Date.now();
    publish({
      ...next,
      clips: next.clips.map(clip => {
        if (clip.clipId !== activeRef.current) return clip;
        const playing = previous.find(p => p.clipId === clip.clipId);
        return playing ? { ...clip, playUrl: playing.playUrl } : clip;
      }),
    });
    return next.clips;
  }, [withToken, publish]);

  // The account, not the token: a renewed token is still the same reader.
  const owner =
    HISTORY_ENABLED && auth.isAuthenticated ? auth.user?.profile?.sub : null;

  useEffect(() => {
    sessionRef.current += 1;
    publish(EMPTY);
    // A list that won't load is not worth interrupting the reader for: the
    // page just keeps showing its examples.
    if (owner) refresh().catch(() => {});
  }, [owner, refresh, publish]);

  // setInterval stops while a phone sleeps, so coming back checks too.
  useEffect(() => {
    if (!owner) return undefined;
    const refreshIfStale = () => {
      if (document.hidden) return;
      if (Date.now() - loadedAtRef.current < LINK_REFRESH_MS) return;
      refresh().catch(() => {});
    };
    const timer = setInterval(refreshIfStale, STALE_CHECK_MS);
    document.addEventListener('visibilitychange', refreshIfStale);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', refreshIfStale);
    };
  }, [owner, refresh]);

  const refreshAfterSave = useCallback(async () => {
    const newest = stateRef.current.clips[0]?.clipId;
    try {
      const clips = await refresh();
      return clips ? clips[0]?.clipId !== newest : null;
    } catch {
      return null;
    }
  }, [refresh]);

  const showMore = useCallback(async () => {
    const { nextAfter } = stateRef.current;
    if (!nextAfter) return;
    const session = sessionRef.current;
    const page = await withToken(token => listHistory(token, nextAfter));
    if (session !== sessionRef.current) return;
    publish({
      ...stateRef.current,
      clips: [...stateRef.current.clips, ...page.items],
      nextAfter: page.nextAfter,
    });
  }, [withToken, publish]);

  const remove = useCallback(
    async clipId => {
      await withToken(token => deleteHistory(token, clipId));
      publish({
        ...stateRef.current,
        clips: stateRef.current.clips.filter(c => c.clipId !== clipId),
      });
    },
    [withToken, publish]
  );

  return useMemo(
    () => ({
      clips: state.clips,
      hasMore: Boolean(state.nextAfter),
      retentionDays: state.retentionDays,
      showMore,
      refresh,
      refreshAfterSave,
      remove,
    }),
    [state, showMore, refresh, refreshAfterSave, remove]
  );
};

export default useHistory;
